/**
 * User API client wrapping the backend /users/* endpoints.
 * Covers public/private profile fetches (optionally syncing the result into
 * UserContext via a dispatch), profile edits, email verification codes,
 * profile picture upload, notification settings, logout, and email
 * unsubscribe. Consumed by TopNavBar, useAuthSync, and the Account,
 * NotificationSettings, VerifyEmail, and EmailUnsubscribe pages.
 */
import { api } from "@/lib/axios";
import { PrivateUser } from "@/types/user";
import { setUserContext, UserAction } from "@/contexts/UserContext";

export const UserAPI = {
    getUserPublic: async (id: string) => {
        const { data } = await api.get<{ user: PrivateUser }>(`/users/${id}`);
        return data;
    },
    getUserPrivate: async (dispatch?: React.Dispatch<UserAction>) => {
        const { data } = await api.get<{ user: PrivateUser }>(`/users`);
        if (dispatch) {
            setUserContext(dispatch, data.user);
        }
        return data;
    },
    editUser: async (instagram: string, bio: string) => {
        const { data } = await api.put<{ user: PrivateUser }>("/users", { instagram, bio });
        return data;
    },
    submitEmail: async (email: string) => {
        const { data } = await api.post("/users/send-email-code", { email });
        return data;
    },
    submitCode: async (email: string, code: string) => {
        const { data } = await api.post<{ user: PrivateUser }>("/users/verify-email-code", {
            email,
            code,
        });
        return data;
    },
    uploadProfilePicture: async (formData: FormData) => {
        const { data } = await api.post<{ user: PrivateUser }>(
            "/users/upload-profile-picture",
            formData,
            {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            }
        );
        return data;
    },
    updateNotificationSettings: async (emailCommEnabled: boolean, smsCommEnabled: boolean) => {
        const { data } = await api.put<{ user: PrivateUser }>("/users/update-comm-settings", {
            emailCommEnabled,
            smsCommEnabled,
        });
        return data;
    },
    logout: async () => {
        const { data } = await api.post("/users/logout");
        return data;
    },
    emailUnsubscribe: async (token: string) => {
        const { data } = await api.get<{ message: string }>(`/users/email-unsubscribe/${token}`);
        return data;
    },
};
